import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Star, Zap, Shield, Clock, Gift, TrendingUp } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { usePremium } from '@/hooks/usePremium';
import { supabase } from '@/integrations/supabase/client';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import AppleSignInButton from '@/components/AppleSignInButton';

interface PremiumModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PremiumModal = ({ open, onOpenChange }: PremiumModalProps) => {
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const { subscribeToPremium } = usePremium();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsAuthenticated(!!user);
    };
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session?.user);
    });

    return () => subscription.unsubscribe();
  }, []);

  const plans = {
    monthly: {
      price: 49,
      period: 'month',
      savings: null,
    },
    yearly: {
      price: 490,
      period: 'year',
      savings: '2 months free',
      monthlyEquivalent: 41,
    }
  };

  const features = [
    {
      icon: <Gift className="h-5 w-5" />,
      title: "25% Off All Orders",
      description: "Save on every single order, every time you're hungry",
    },
    {
      icon: <Zap className="h-5 w-5" />,
      title: "Priority Delivery",
      description: "Get your food faster with premium delivery priority",
    },
    {
      icon: <Shield className="h-5 w-5" />,
      title: "Free Delivery",
      description: "R0 delivery fees on all orders, no minimum spend",
    },
    {
      icon: <Star className="h-5 w-5" />,
      title: "Exclusive Deals",
      description: "Access premium-only promotions and early bird offers",
    },
    {
      icon: <Clock className="h-5 w-5" />,
      title: "24/7 Support",
      description: "Dedicated premium support team at your service",
    },
    {
      icon: <TrendingUp className="h-5 w-5" />,
      title: "Double Rewards",
      description: "Earn 2x loyalty points on every purchase",
    }
  ];

  const handleUpgrade = async () => {
    setIsProcessing(true);
    
    try {
      // Initialize Paystack payment
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        setIsProcessing(false);
        return;
      }

      const amount = selectedPlan === 'monthly' ? 4900 : 49000; // in kobo/cents
      const reference = `premium_${user.id}_${Date.now()}`;

      const PaystackPop = (window as any).PaystackPop;
      if (!PaystackPop) {
        // Dynamically load Paystack script
        const script = document.createElement('script');
        script.src = 'https://js.paystack.co/v2/inline.js';
        script.onload = () => initiatePayment(user.email!, amount, reference);
        document.head.appendChild(script);
      } else {
        initiatePayment(user.email, amount, reference);
      }
    } catch (err) {
      console.error('Payment init error:', err);
      setIsProcessing(false);
    }
  };

  const initiatePayment = (email: string, amount: number, reference: string) => {
    const PaystackPop = (window as any).PaystackPop;
    const paystackKey = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY;

    if (!paystackKey) {
      setIsProcessing(false);
      return;
    }

    const handler = PaystackPop.setup({
      key: paystackKey,
      email,
      amount,
      currency: 'ZAR',
      ref: reference,
      callback: async (response: { reference: string }) => {
        // Payment successful — now activate via server
        const success = await subscribeToPremium(selectedPlan, response.reference);
        setIsProcessing(false);
        if (success) {
          onOpenChange(false);
        }
      },
      onClose: () => {
        setIsProcessing(false);
      },
    });
    handler.openIframe();
  };

  const currentPlan = plans[selectedPlan];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-dark-surface border-border/40">
        <DialogHeader>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Crown className="h-8 w-8 text-neon-yellow" />
            <DialogTitle className="text-3xl font-bold bg-gradient-neon bg-clip-text text-transparent">
              LUMZA Premium
            </DialogTitle>
          </div>
          <DialogDescription className="text-center text-base">
            Unlock exclusive benefits and save on every order
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Pricing Toggle */}
          <div className="flex justify-center">
            <div className="inline-flex items-center bg-background/50 rounded-full p-1 border border-border/40">
              <button
                onClick={() => setSelectedPlan('monthly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedPlan === 'monthly'
                    ? 'bg-gradient-neon text-midnight'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setSelectedPlan('yearly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all relative ${
                  selectedPlan === 'yearly'
                    ? 'bg-gradient-neon text-midnight'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Yearly
                <Badge className="absolute -top-2 -right-2 bg-neon-green text-midnight text-xs">
                  Save 17%
                </Badge>
              </button>
            </div>
          </div>

          {/* Price Display */}
          <div className="text-center p-6 bg-gradient-to-br from-neon-purple/10 to-neon-blue/10 rounded-xl border border-border/40">
            <div className="flex items-baseline justify-center gap-2">
              <span className="text-4xl font-bold">R{currentPlan.price}</span>
              <span className="text-muted-foreground">/ {currentPlan.period}</span>
            </div>
            {selectedPlan === 'yearly' && plans.yearly.monthlyEquivalent && (
              <div className="mt-2 text-sm">
                <span className="text-neon-green font-semibold">
                  Only R{plans.yearly.monthlyEquivalent}/month
                </span>
                <span className="text-muted-foreground"> • {currentPlan.savings}</span>
              </div>
            )}
          </div>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 gap-4">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="p-4 rounded-lg bg-background/50 border border-border/40 hover:border-primary/40 transition-colors"
              >
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-neon/20 flex items-center justify-center text-neon-yellow">
                    {feature.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold mb-1">{feature.title}</h4>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Savings Calculator */}
          <div className="p-4 bg-gradient-to-r from-neon-green/10 to-neon-blue/10 rounded-lg border border-neon-green/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Average monthly savings</p>
                <p className="text-2xl font-bold text-neon-green">~R450</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Based on 8 orders per month
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground mb-1">Break-even point</p>
                <p className="text-xl font-bold">1 order</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Instant value from first order
                </p>
              </div>
            </div>
          </div>

          {/* CTA Buttons / Sign Up Flow */}
          {!isAuthenticated ? (
            <div className="space-y-4">
              <div className="text-center p-4 bg-muted/30 rounded-lg border border-border/40">
                <p className="text-sm text-muted-foreground mb-3">
                  Sign up or log in to unlock Premium benefits
                </p>
                <GoogleSignInButton className="mb-3" />
                <AppleSignInButton className="mb-3" />
                <Button
                  variant="outline"
                  onClick={() => {
                    onOpenChange(false);
                    navigate('/auth');
                  }}
                  className="w-full"
                >
                  Sign up with Email
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleUpgrade}
                disabled={isProcessing}
                className="flex-1 bg-gradient-neon text-midnight hover:opacity-90 h-12 text-base font-semibold"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-midnight mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Crown className="mr-2 h-5 w-5" />
                    Upgrade to Premium
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="sm:w-32"
              >
                Maybe Later
              </Button>
            </div>
          )}

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-4 pt-4 border-t border-border/40">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Shield className="h-4 w-4 text-neon-green" />
              <span>Cancel anytime</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Check className="h-4 w-4 text-neon-green" />
              <span>No hidden fees</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Star className="h-4 w-4 text-neon-green" />
              <span>Trusted by 10,000+ members</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PremiumModal;
